import { useState, useEffect, useMemo, useCallback } from "react";
import { Clock, Target, Filter, Coffee, Home } from "lucide-react";
import { Select } from "antd";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../../components/ui/tabs";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ActivityLogTab from "./ActivityLogTab";
import AttendanceLogTab from "./AttendanceLogTab";
import EmployeeCheckinTab from "./EmployeeCheckinTab";
import AddSpentHoursTab from "./AddSpentHoursTab";
import {
  DropdownService,
  PtsEmployeeService,
  PtsAttendanceService,
  DynamicModuleService,
  SchemaService,
} from "@/services/APIs";
import { LoginOutlined, LogoutOutlined } from "@ant-design/icons";
import { getEmployeeId } from "@/lib/storeManager";
import {
  isEmployee,
  isPTS_PM,
  isPTS_BA,
  isPTSIT,
} from "../../utils/rolePermissions";
import { notify } from "@/components/common/Notification";
import { Button } from "../../components/ui/button";

// const ATTENDANCE_SCHEMA_NAME = "PTS Attendance";
const EMPLOYEE_SCHEMA_NAME = "PTS Emp";
const EMPLOYEE_CHECKIN_SCHEMA_NAME = "PTS Employee Checkin";

/** Format date as "YYYY-MM-DD HH:mm:ss" for WFH check-in/out */
const formatDateTimeLocal = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")} ${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}:${String(date.getSeconds()).padStart(2, "0")}`;

const EmployeeDashboard = () => {
  // Get employee ID from localStorage
  const employeeId = getEmployeeId();

  // Check user role permissions
  // PTS_IT uses the same self-scoped dashboard as PTS_Employee (no employee Filters card).
  const userIsEmployee = isEmployee() || isPTSIT();
  const userIsPMOrBA = isPTS_PM() || isPTS_BA();

  // Set selected employee based on role
  // If employee/IT role: always use their own employeeId (cannot change)
  // If admin/user role: allow selection (default to employeeId if available, else "all")
  const [selectedEmployee, setSelectedEmployee] = useState<string>(() => {
    if (userIsEmployee && employeeId) {
      return employeeId;
    }
    return employeeId ?? "all";
  });

  // Update selectedEmployee when employeeId is available (only for employee/IT role)
  // For admin/user roles, allow manual selection changes
  useEffect(() => {
    if (userIsEmployee && employeeId) {
      // Lock to own employee ID for employee / PTS_IT
      setSelectedEmployee(employeeId);
    } else if (userIsPMOrBA && employeeId) {
      // PM/BA: no employee filter on dashboard — always scope to own employee record
      setSelectedEmployee(employeeId);
    } else if (!selectedEmployee && employeeId) {
      // For admin/user, set default if not already set
      setSelectedEmployee(employeeId);
    }
  }, [employeeId, userIsEmployee, userIsPMOrBA, selectedEmployee]);

  const queryClient = useQueryClient();
  const [wfhLoading, setWfhLoading] = useState<"IN" | "OUT" | null>(null);

  // Employee ID for WFH check-in/out payload: current user when employee/IT, or selected employee when admin
  const wfhEmployeeId = userIsEmployee
    ? employeeId
    : selectedEmployee !== "all"
      ? selectedEmployee
      : employeeId;

  const handleWfhCheckInOut = useCallback(
    async (logtype: "IN" | "OUT") => {
      if (!wfhEmployeeId) {
        notify.error(
          "No employee selected. Please select an employee or ensure you are linked to an employee."
        );
        return;
      }
      setWfhLoading(logtype);
      try {
        await DynamicModuleService.createItem(EMPLOYEE_CHECKIN_SCHEMA_NAME, {
          datetime: formatDateTimeLocal(new Date()),
          employee: wfhEmployeeId,
          logtype,
        });
        // Generate attendance for current date and employee after check-in/out success
        const today = formatDateTimeLocal(new Date()).slice(0, 10); // "YYYY-MM-DD"
        await PtsAttendanceService.generateAttendance(today, wfhEmployeeId);
        notify.success(
          logtype === "IN"
            ? "WFH Check-In recorded successfully."
            : "WFH Check-Out recorded successfully."
        );
        queryClient.invalidateQueries({ queryKey: ["employee-checkin-data"] });
        queryClient.invalidateQueries({ queryKey: ["employee-dashboard"] });
      } catch (err: any) {
        notify.error(
          err?.message ??
            (logtype === "IN"
              ? "WFH Check-In failed."
              : "WFH Check-Out failed.")
        );
      } finally {
        setWfhLoading(null);
      }
    },
    [wfhEmployeeId, queryClient]
  );

  // Applied filters (used for actual query)
  // const [] = useState<{
  //   date: string;
  //   employee: string;
  // }>({
  //   date: selectedDate,
  //   employee: "all",
  // });

  console.log("datetime", new Date().toISOString());
  // Fetch employee dropdown
  const { data: employeeOptions } = useQuery({
    queryKey: ["employee-dropdown", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(EMPLOYEE_SCHEMA_NAME),
    enabled: !!EMPLOYEE_SCHEMA_NAME,
  });

  // Fetch employee data to get first_name and last_name
  const { data: employeeDataResponse } = useQuery({
    queryKey: ["employee-data", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => {
      return DynamicModuleService.getGridColItems(
        EMPLOYEE_SCHEMA_NAME,
        1,
        1000,
        {}
      );
    },
    enabled: !!EMPLOYEE_SCHEMA_NAME,
  });

  // Extract employee records
  const employeeRecords = useMemo(() => {
    if (!employeeDataResponse) {
      return [];
    }
    if (Array.isArray(employeeDataResponse)) {
      return employeeDataResponse;
    }
    if (
      employeeDataResponse.results &&
      Array.isArray(employeeDataResponse.results)
    ) {
      return employeeDataResponse.results;
    }
    return [];
  }, [employeeDataResponse]);

  // Get employee column grid data to find first_name and last_name fields
  const { data: employeeColumnGridData } = useQuery({
    queryKey: ["columnGridData", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => SchemaService.getColumnGridData(EMPLOYEE_SCHEMA_NAME),
  });

  // Find employee field names
  const findEmployeeFieldName = useCallback(
    (searchTerms: string[]) => {
      if (!employeeColumnGridData?.grid) {
        return null;
      }
      for (const field of employeeColumnGridData.grid) {
        const fieldName = field.field_name.toLowerCase();
        const fieldTitle = field.field_title?.toLowerCase() ?? "";
        if (
          searchTerms.some(term => {
            const lowerTerm = term.toLowerCase();
            return (
              fieldName.includes(lowerTerm) ?? fieldTitle.includes(lowerTerm)
            );
          })
        ) {
          return field.field_name;
        }
      }
      return null;
    },
    [employeeColumnGridData]
  );

  const employeeFirstNameField = findEmployeeFieldName([
    "first_name",
    "firstname",
    "fname",
  ]);
  const employeeLastNameField = findEmployeeFieldName([
    "last_name",
    "lastname",
    "lname",
  ]);

  // Transform employee options to show first name + last name
  const employeeOptionsWithNames = useMemo(() => {
    if (!employeeOptions || !employeeRecords.length) {
      return employeeOptions ?? [];
    }

    return employeeOptions.map((employee: { value: string; label: string }) => {
      // Find the employee record by ID
      const employeeRecord = employeeRecords.find(
        (record: any) =>
          String(
            record?.id ?? record?.ID ?? record?.Id ?? record?._id ?? ""
          ) === String(employee.value)
      );

      if (employeeRecord) {
        // Get first name and last name from the record
        const firstName =
          (employeeFirstNameField && employeeRecord[employeeFirstNameField]) ??
          employeeRecord.first_name ??
          employeeRecord.firstname ??
          employeeRecord.fname ??
          "";
        const lastName =
          (employeeLastNameField && employeeRecord[employeeLastNameField]) ??
          employeeRecord.last_name ??
          employeeRecord.lastname ??
          employeeRecord.lname ??
          "";

        // Combine first name and last name
        const fullName = `${firstName} ${lastName}`.trim();

        return {
          value: employee.value,
          label: fullName || employee.label, // Fallback to original label if names not found
        };
      }

      // Fallback to original label if employee record not found
      return employee;
    });
  }, [
    employeeOptions,
    employeeRecords,
    employeeFirstNameField,
    employeeLastNameField,
  ]);

  // Get employee first and last name by ID
  const getEmployeeNamesById = useCallback(
    (id: string | number | null | undefined) => {
      if (!id) {
        return { firstName: "-", lastName: "" };
      }

      const employee = employeeRecords.find(
        (record: any) =>
          String(
            record?.id ?? record?.ID ?? record?.Id ?? record?._id ?? ""
          ) === String(id)
      );

      if (!employee) {
        // Fallback to dropdown label if employee record not found
        const dropdownEmployee = employeeOptions?.find(
          (item: { value: string; label: string }) =>
            String(item.value) === String(id)
        );
        if (dropdownEmployee?.label) {
          const nameParts = dropdownEmployee.label.split(" ");
          return {
            firstName: nameParts[0] ?? "-",
            lastName: nameParts.slice(1).join(" ") ?? "",
          };
        }
        return { firstName: "-", lastName: "" };
      }

      const firstName =
        (employeeFirstNameField && employee[employeeFirstNameField]) ??
        employee.first_name ??
        employee.firstname ??
        employee.fname ??
        "";
      const lastName =
        (employeeLastNameField && employee[employeeLastNameField]) ??
        employee.last_name ??
        employee.lastname ??
        employee.lname ??
        "";

      return {
        firstName: firstName ?? "-",
        lastName: lastName ?? "",
      };
    },
    [
      employeeRecords,
      employeeOptions,
      employeeFirstNameField,
      employeeLastNameField,
    ]
  );

  // Get employee name by ID - use selectedEmployee for admin/user roles
  const employeeName = useMemo(() => {
    const employeeIdToUse = userIsEmployee ? employeeId : selectedEmployee;
    if (!employeeIdToUse || employeeIdToUse === "all") {
      return null;
    }
    const names = getEmployeeNamesById(employeeIdToUse);
    return `${names.firstName} ${names.lastName}`.trim() || null;
  }, [employeeId, selectedEmployee, userIsEmployee, getEmployeeNamesById]);

  // Fetch employee dashboard data
  const { data: dashboardData, isLoading: isLoadingDashboard } = useQuery({
    queryKey: ["employee-dashboard", selectedEmployee],
    queryFn: () => {
      if (!selectedEmployee || selectedEmployee === "all") {
        return null;
      }
      return PtsEmployeeService.getEmployeeDashboard(selectedEmployee);
    },
    enabled: !!selectedEmployee && selectedEmployee !== "all",
  });

  // Get column grid data to find field names
  // const { data: columnGridData } = useQuery({
  //   queryKey: ["columnGridData", ATTENDANCE_SCHEMA_NAME],
  //   queryFn: () => SchemaService.getColumnGridData(ATTENDANCE_SCHEMA_NAME),
  // });

  // Fetch attendance data
  // const { data: attendanceDataResponse } = useQuery({
  //   queryKey: [
  //     "employee-dashboard-data",
  //     ATTENDANCE_SCHEMA_NAME,
  //     appliedFilters,
  //   ],
  //   queryFn: () => {
  //     const filter: any = {};
  //     if (appliedFilters.date) {
  //       filter.start_date = appliedFilters.date;
  //       filter.end_date = appliedFilters.date;
  //     }
  //     if (appliedFilters.employee && appliedFilters.employee !== "all") {
  //       filter.employee = appliedFilters.employee;
  //     }
  //     return DynamicModuleService.getGridColItems(
  //       ATTENDANCE_SCHEMA_NAME,
  //       1,
  //       1000,
  //       filter
  //     );
  //   },
  //   enabled: !!ATTENDANCE_SCHEMA_NAME,
  // });

  // Extract attendance data from response
  // const attendanceData = useMemo(() => {
  //   if (!attendanceDataResponse) {
  //     return [];
  //   }
  //   if (Array.isArray(attendanceDataResponse)) {
  //     return attendanceDataResponse;
  //   }
  //   if (
  //     attendanceDataResponse.results &&
  //     Array.isArray(attendanceDataResponse.results)
  //   ) {
  //     return attendanceDataResponse.results;
  //   }
  //   return [];
  // }, [attendanceDataResponse]);

  // Find field names from schema
  // const findFieldName = useCallback(
  //   (searchTerms: string[]) => {
  //     if (!columnGridData?.grid) {
  //       return null;
  //     }
  //     for (const field of columnGridData.grid) {
  //       const fieldName = field.field_name.toLowerCase();
  //       const fieldTitle = field.field_title?.toLowerCase() ?? "";
  //       if (
  //         searchTerms.some(
  //           term =>
  //             fieldName.includes(term.toLowerCase()) ??
  //             fieldTitle.includes(term.toLowerCase())
  //         )
  //       ) {
  //         return field.field_name;
  //       }
  //     }
  //     return null;
  //   },
  //   [columnGridData]
  // );

  // Get field names
  // const inTimeField = findFieldName([
  //   "in_time",
  //   "intime",
  //   "check_in",
  //   "checkin",
  //   "first_check_in",
  // ]);
  // const outTimeField = findFieldName([
  //   "out_time",
  //   "outtime",
  //   "check_out",
  //   "checkout",
  //   "final_check_out",
  // ]);
  // const breakInField = findFieldName(["break_in", "breakin", "break_in_time"]);
  // const breakOutField = findFieldName([
  //   "break_out",
  //   "breakout",
  //   "break_out_time",
  // ]);
  // const totalHoursField = findFieldName([
  //   "total_hours",
  //   "totalhours",
  //   "hours",
  //   "work_hours",
  //   "productive_hours",
  // ]);
  // const breakHoursField = findFieldName([
  //   "break_hours",
  //   "breakhours",
  //   "break_duration",
  //   "total_break",
  // ]);
  // const biometricIdField = findFieldName([
  //   "biometric_id",
  //   "biometricid",
  //   "emp_id",
  //   "employee_id",
  // ]);

  // Format time helper
  // const formatTime = (time: string | null | undefined) => {
  //   if (!time || time === "-" || time === "null" || time === "undefined") {
  //     return "-";
  //   }
  //   try {
  //     if (
  //       typeof time === "string" &&
  //       time.includes(":") &&
  //       !time.includes("T")
  //     ) {
  //       return time;
  //     }
  //     const d = new Date(time);
  //     if (isNaN(d.getTime())) {
  //       return "-";
  //     }
  //     return d.toLocaleTimeString("en-US", {
  //       hour: "2-digit",
  //       minute: "2-digit",
  //       hour12: true,
  //     });
  //   } catch {
  //     return "-";
  //   }
  // };

  // Calculate hours from time strings
  // const calculateHours = (hours: string | number | null | undefined) => {
  //   if (!hours) {
  //     return 0;
  //   }
  //   if (typeof hours === "number") {
  //     return hours;
  //   }
  //   if (typeof hours === "string") {
  //     const num = parseFloat(hours.replace("h", "").trim());
  //     return isNaN(num) ? 0 : num;
  //   }
  //   return 0;
  // };

  // Process attendance data for dashboard
  // const dashboardData = useMemo(() => {
  //   if (!attendanceData || attendanceData.length === 0) {
  //     return {
  //       totalHours: 0,
  //       breakHours: 0,
  //       productiveHours: 0,
  //       monthlyDiff: 0,
  //       firstCheckIn: null,
  //       finalCheckOut: null,
  //       biometricId: null,
  //       breakSessions: [],
  //       targetHours: 9, // Default target
  //       isOnTime: true,
  //       isCompleted: true,
  //     };
  //   }

  //   // For "all employees", aggregate data; for single employee, use first record
  //   const records =
  //     appliedFilters.employee === "all"
  //       ? attendanceData
  //       : attendanceData.slice(0, 1);

  //   let totalHours = 0;
  //   let breakHours = 0;
  //   let firstCheckIn: string | null = null;
  //   let finalCheckOut: string | null = null;
  //   let biometricId: string | null = null;
  //   const breakSessions: Array<{
  //     breakNumber: number;
  //     breakIn: string;
  //     breakOut: string;
  //     duration: number;
  //   }> = [];

  //   records.forEach((record: any, index: number) => {
  //     const recordTotalHours = calculateHours(
  //       (totalHoursField && record[totalHoursField]) ??
  //         record.total_hours ??
  //         record.hours ??
  //         0
  //     );
  //     const recordBreakHours = calculateHours(
  //       (breakHoursField && record[breakHoursField]) ??
  //         record.break_hours ??
  //         record.break_duration ??
  //         0
  //     );

  //     totalHours += recordTotalHours;
  //     breakHours += recordBreakHours;

  //     if (index === 0) {
  //       firstCheckIn =
  //         (inTimeField && record[inTimeField]) ??
  //         record.in_time ??
  //         record.intime ??
  //         record.check_in ??
  //         null;
  //       finalCheckOut =
  //         (outTimeField && record[outTimeField]) ??
  //         record.out_time ??
  //         record.outtime ??
  //         record.check_out ??
  //         null;
  //       biometricId =
  //         (biometricIdField && record[biometricIdField]) ??
  //         record.biometric_id ??
  //         record.emp_id ??
  //         record.employee_id ??
  //         null;
  //     }

  //     // Extract break sessions (simplified - assuming breaks are in the record)
  //     // In a real scenario, you might need to parse break data differently
  //     const breakIn =
  //       (breakInField && record[breakInField]) ?? record.break_in ?? null;
  //     const breakOut =
  //       (breakOutField && record[breakOutField]) ?? record.break_out ?? null;

  //     if (breakIn && breakOut) {
  //       breakSessions.push({
  //         breakNumber: breakSessions.length + 1,
  //         breakIn: formatTime(breakIn),
  //         breakOut: formatTime(breakOut),
  //         duration: 0.25, // Default, should be calculated from actual times
  //       });
  //     }
  //   });

  //   const productiveHours = totalHours - breakHours;
  //   const targetHours = 9;
  //   const monthlyDiff = totalHours - targetHours; // Simplified calculation

  //   // Mock break sessions if none found (for demo purposes)
  //   const finalBreakSessions =
  //     breakSessions.length > 0
  //       ? breakSessions
  //       : [
  //           {
  //             breakNumber: 1,
  //             breakIn: "10:30 AM",
  //             breakOut: "10:45 AM",
  //             duration: 0.25,
  //           },
  //           {
  //             breakNumber: 2,
  //             breakIn: "12:30 PM",
  //             breakOut: "01:15 PM",
  //             duration: 0.75,
  //           },
  //           {
  //             breakNumber: 3,
  //             breakIn: "04:00 PM",
  //             breakOut: "04:15 PM",
  //             duration: 0.25,
  //           },
  //         ];

  //   const totalBreakDuration = finalBreakSessions.reduce(
  //     (sum, session) => sum + session.duration,
  //     0
  //   );

  //   return {
  //     totalHours: totalHours || 8.3,
  //     breakHours: breakHours || totalBreakDuration || 1.25,
  //     productiveHours: productiveHours || 7.55,
  //     monthlyDiff: monthlyDiff || -12.5,
  //     firstCheckIn: firstCheckIn || "09:05 AM",
  //     finalCheckOut: finalCheckOut || "06:10 PM",
  //     biometricId: biometricId || "EMP001",
  //     breakSessions: finalBreakSessions,
  //     targetHours,
  //     isOnTime: true,
  //     isCompleted: true,
  //   };
  // }, [
  //   attendanceData,
  //   appliedFilters.employee,
  //   inTimeField,
  //   outTimeField,
  //   totalHoursField,
  //   breakHoursField,
  //   biometricIdField,
  //   breakInField,
  //   breakOutField,
  // ]);

  // Apply filters when they change
  // useEffect(() => {
  //   setAppliedFilters({
  //     date: selectedDate,
  //     employee: selectedEmployee,
  //   });
  // }, [selectedDate, selectedEmployee]);

  // const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   setSelectedDate(e.target.value);
  // };

  /** Admin/HR: always show filter row. Employee or PM/BA: only when WFH is enabled for today (API). */
  const showFiltersBar =
    (!userIsEmployee && !userIsPMOrBA) ||
    (!!employeeId &&
      !!dashboardData?.today_hours?.today_wfh &&
      (userIsEmployee || userIsPMOrBA));

  return (
    <div className="min-h-screen overflow-y-auto bg-slate-50 p-4 md:p-0">
      {/* Header */}
      <div
        className={`flex items-center justify-between ${userIsEmployee || userIsPMOrBA ? "mb-6" : ""}`}
      >
        <div>
          <h1 className="text-2xl font-bold text-[#111827]">
            {employeeName ? `Hi, ${employeeName}` : "Employee Dashboard"}
          </h1>
          <p className="text-sm text-[#627384]">
            Welcome back! Here&apos;s your daily overview
          </p>
        </div>
      </div>

      {/* Filters bar: admin/HR sees Filters + Select; PM/BA and employees only see this card when WFH applies (today_wfh). */}
      {showFiltersBar && (
        <div className="mb-6 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-white p-4">
          <div className="flex flex-wrap items-center gap-3">
            {!userIsEmployee && !userIsPMOrBA ? (
              <>
                <div className="flex items-center gap-2 text-xs text-slate-600">
                  <Filter size={14} />
                  Filters:
                </div>
                <Select
                  value={selectedEmployee}
                  onChange={setSelectedEmployee}
                  placeholder="All Employees"
                  className="h-9 w-full sm:w-[180px]"
                  allowClear
                  showSearch
                  filterOption={(input, option) => {
                    const label = String(option?.label ?? "");
                    return label.toLowerCase().includes(input.toLowerCase());
                  }}
                  options={[
                    { value: "all", label: "All Employees" },
                    ...(employeeOptionsWithNames?.map(
                      (employee: { value: string; label: string }) => ({
                        value: String(employee.value),
                        label: employee.label,
                      })
                    ) ?? []),
                  ]}
                />
              </>
            ) : (
              <div className="flex items-center gap-2 pl-1 text-sm font-medium text-slate-800">
                <Home size={16} />
                Work From Home
              </div>
            )}
          </div>
          {wfhEmployeeId && dashboardData?.today_hours?.today_wfh && (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="hover:bg-primary h-9 rounded-lg border-slate-300 bg-green-50 px-4 text-xs font-medium text-slate-700 shadow-sm hover:text-white"
                onClick={() => handleWfhCheckInOut("IN")}
                disabled={
                  wfhLoading !== null ||
                  !!dashboardData?.check_in_out?.first_checkin
                }
              >
                <LoginOutlined className="mr-1.5 align-middle text-green-600" />
                {wfhLoading === "IN" ? "Checking In..." : "WFH Check-In"}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="hover:bg-primary h-9 rounded-lg border-slate-300 bg-red-50 px-4 text-xs font-medium text-slate-700 shadow-sm hover:text-white"
                onClick={() => handleWfhCheckInOut("OUT")}
                disabled={
                  wfhLoading !== null ||
                  !!dashboardData?.check_in_out?.checkout ||
                  !dashboardData?.check_in_out?.first_checkin
                }
              >
                <LogoutOutlined className="mr-1.5 align-middle text-red-500" />
                {wfhLoading === "OUT" ? "Checking Out..." : "WFH Check-Out"}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Message when all employees is selected and user is admin/user (not employee) */}
      {selectedEmployee === "all" && !userIsEmployee && !userIsPMOrBA && (
        <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 p-4">
          <p className="text-sm text-blue-800">
            Please select an employee from the filter above to view their
            dashboard data.
          </p>
        </div>
      )}

      {/* Daily Summary + Tracker */}
      <div className="mb-6 grid rounded-xl border bg-white md:gap-6 lg:grid-cols-2">
        {/* Daily Summary */}
        <div className="p-3 md:p-6">
          <div className="mb-4 flex items-center gap-2 text-sm font-medium text-slate-800">
            <Clock size={16} />
            Daily Summary
          </div>

          <div className="flex flex-wrap gap-4">
            <SummaryPill
              label="Total Hours"
              value={dashboardData?.today_hours?.total_hours ?? "0h"}
              bg="bg-primary/90 text-white"
            />
            <SummaryPill
              label="Break"
              value={
                dashboardData?.today_hours?.break_hours
                  ? `${dashboardData.today_hours.break_hours}`
                  : "0m"
              }
              bg="bg-amber-100 text-amber-800"
            />
            <SummaryPill
              label="Productive"
              value={dashboardData?.today_hours?.productive_hours ?? "0h"}
              bg="bg-blue-100 text-blue-800"
            />
            <SummaryPill
              label="Monthly Diff"
              value={dashboardData?.today_hours?.monthly_diff ?? "00:00"}
              bg={
                dashboardData?.today_hours?.monthly_diff?.startsWith("-")
                  ? "bg-rose-100 text-rose-700"
                  : dashboardData?.today_hours?.monthly_diff?.startsWith("+")
                    ? "bg-emerald-100 text-emerald-800"
                    : "bg-rose-100 text-rose-700"
              }
            />
          </div>
        </div>

        {/* Spent Hours Tracker */}
        <div className="p-6">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
              <Target size={16} />
              Spent Hours Tracker
            </div>

            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] text-emerald-700">
              On Track
            </span>
          </div>

          <p className="mb-2 text-xs text-slate-500">Progress</p>

          <div className="mb-3 h-2 w-full rounded-full bg-slate-200">
            <div
              className="h-2 rounded-full bg-[#1d446e] transition-all"
              style={{
                width: `${
                  dashboardData?.progress?.percentage
                    ? Math.min(dashboardData.progress.percentage, 100)
                    : 0
                }%`,
              }}
            />
          </div>

          <div className="flex items-center justify-between text-xs">
            <span className="font-medium text-slate-800">
              {dashboardData?.progress?.current ?? "0h"} /{" "}
              {dashboardData?.progress?.goal ?? "0h"}
            </span>

            <span className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1 text-emerald-700">
              Remaining <b>{dashboardData?.progress?.remaining ?? "0h"}</b>
            </span>
          </div>
        </div>
      </div>

      {/* Check-in / Check-out */}
      <div className="mb-6 grid gap-6 md:grid-cols-3">
        <InfoCard
          icon={<LoginOutlined className="text-green-600" />}
          title="Check-In"
          time={dashboardData?.check_in_out?.first_checkin ?? "-"}
          subtitle="Biometric ID verified"
          badge="On Time"
          badgeColor="bg-green-100 text-green-700"
        />

        <InfoCard
          icon={<LogoutOutlined className="text-secondary" />}
          title="Expected Check-Out"
          time={dashboardData?.check_in_out?.expected_checkout ?? "-"}
          subtitle="Expected checkout time"
          // badge="Pending"
          badgeColor="bg-orange-100 text-orange-600"
        />
        <InfoCard
          icon={<LogoutOutlined className="text-red-500" />}
          title="Check-Out"
          time={dashboardData?.check_in_out?.checkout ?? "-"}
          subtitle="Biometric ID verified"
          badge="Pending"
          badgeColor="bg-orange-100 text-orange-600"
        />
      </div>

      {/* Break Sessions */}
      <div className="rounded-xl border bg-white p-6">
        <div className="mb-4 flex items-center gap-2 text-sm font-medium text-slate-800">
          <Coffee size={16} />
          Break Sessions
        </div>

        <table className="w-full text-xs">
          <thead>
            <tr className="border-b text-left text-slate-500">
              <th className="py-2">Break #</th>
              <th>Break-In</th>
              <th>Break-Out</th>
              <th className="text-right">Duration</th>
            </tr>
          </thead>

          <tbody>
            {isLoadingDashboard ? (
              <tr>
                <td
                  colSpan={4}
                  className="py-4 text-center text-xs text-slate-500"
                >
                  Loading break sessions...
                </td>
              </tr>
            ) : !dashboardData?.break_sessions ||
              dashboardData.break_sessions.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="py-4 text-center text-xs text-slate-500"
                >
                  No break sessions found
                </td>
              </tr>
            ) : (
              dashboardData.break_sessions.map(
                (
                  session: {
                    break_in: string | null;
                    break_out: string | null;
                    duration: string;
                  },
                  index: number
                ) => {
                  // Create unique key from session data without using index
                  const uniqueKey = `break-session-${session.break_in ?? "null"}-${session.break_out ?? "null"}-${session.duration ?? "null"}`;
                  return (
                    <tr key={uniqueKey} className="border-b last:border-none">
                      <td className="py-3">{index + 1}</td>
                      <td>{session.break_in ?? "-"}</td>
                      <td className="text-emerald-600">
                        {session.break_out ?? "-"}
                      </td>
                      <td className="text-right">
                        {session.duration ? `${session.duration}` : "-"}
                      </td>
                    </tr>
                  );
                }
              )
            )}
          </tbody>
        </table>

        <div className="mt-4 flex justify-between text-xs font-medium">
          <span className="text-slate-700">Total Break Duration</span>
          <span className="text-amber-600">
            {dashboardData?.today_hours?.break_hours
              ? `${dashboardData.today_hours.break_hours}`
              : "0"}
          </span>
        </div>
      </div>

      {/* Add Spent Hours Section */}
      <div className="mt-6">
        <AddSpentHoursTab />
      </div>

      {/* Tabs Section */}
      <div className="mt-6">
        <Tabs defaultValue="activity-log" className="w-full">
          <div className="mb-4 w-full overflow-x-auto">
            <TabsList className="h-[3rem] w-max min-w-full bg-gray-100 p-[6px]">
              <TabsTrigger
                value="activity-log"
                className="data-[state=active]:border-primary w-full whitespace-nowrap data-[state=active]:border-b-0 data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm"
              >
                Activity Log
              </TabsTrigger>
              <TabsTrigger
                value="attendance-log"
                className="w-full whitespace-nowrap data-[state=active]:border-b-0 data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm"
              >
                Attendance Log
              </TabsTrigger>
              <TabsTrigger
                value="employee-checkin"
                className="w-full whitespace-nowrap data-[state=active]:border-b-0 data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm"
              >
                Employee Checkin
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="activity-log" className="mt-0">
            <ActivityLogTab
              selectedEmployeeId={
                selectedEmployee === "all" ? null : selectedEmployee
              }
            />
          </TabsContent>

          <TabsContent value="attendance-log" className="mt-0">
            <AttendanceLogTab
              selectedEmployeeId={
                selectedEmployee === "all" ? null : selectedEmployee
              }
            />
          </TabsContent>

          <TabsContent value="employee-checkin" className="mt-0">
            <EmployeeCheckinTab
              selectedEmployeeId={
                selectedEmployee === "all" ? null : selectedEmployee
              }
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>

    // <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-6">
    //   {/* Header */}
    //   <div className="space-y-2">
    //     <h1 className="text-3xl font-bold text-[#111827]">Employee Dashboard</h1>
    //     <p className="text-sm text-[#627384]">
    //       Welcome back! Here's your daily overview
    //     </p>
    //   </div>

    //   {/* Filters Section */}
    //   <Card className="border border-gray-200 bg-white">
    //     <CardContent className="p-4">
    //       <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
    //         <div className="flex items-center gap-2">
    //           <Filter className="h-4 w-4 text-gray-500" />
    //           <Label className="text-sm font-medium text-gray-700">Filters</Label>
    //         </div>
    //         <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
    //           <div className="space-y-2">
    //             <Label htmlFor="employee" className="text-sm text-gray-700">
    //               Employee
    //             </Label>
    //             <Select
    //               value={selectedEmployee}
    //               onValueChange={setSelectedEmployee}
    //             >
    //               <SelectTrigger className="w-full sm:w-[200px]">
    //                 <SelectValue placeholder="Select employee" />
    //               </SelectTrigger>
    //               <SelectContent>
    //                 <SelectItem value="all">All Employees</SelectItem>
    //                 {employeeOptions?.map(
    //                   (employee: { value: string; label: string }) => (
    //                     <SelectItem key={employee.value} value={String(employee.value)}>
    //                       {employee.label}
    //                     </SelectItem>
    //                   )
    //                 )}
    //               </SelectContent>
    //             </Select>
    //           </div>
    //           <div className="space-y-2">
    //             <Label htmlFor="date" className="text-sm text-gray-700">
    //               Date
    //             </Label>
    //             <div className="relative">
    //               <input
    //                 type="date"
    //                 id="date"
    //                 value={selectedDate}
    //                 onChange={handleDateChange}
    //                 className="w-full rounded-lg border border-gray-300 bg-[#fafafa] px-3 py-2 pl-10 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
    //               />
    //               <CalendarIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
    //             </div>
    //           </div>
    //         </div>
    //       </div>
    //     </CardContent>
    //   </Card>

    //   {/* Dashboard Cards Grid */}
    //   <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
    //     {/* Daily Summary Card */}
    //     <Card className="border border-gray-200 bg-white">
    //       <CardHeader>
    //         <CardTitle className="flex items-center gap-2 text-lg font-semibold">
    //           <Clock className="h-5 w-5 text-gray-600" />
    //           Daily Summary
    //         </CardTitle>
    //       </CardHeader>
    //       <CardContent>
    //         <div className="grid grid-cols-2 gap-4">
    //           <div className="flex flex-col items-center justify-center rounded-full bg-[#1D4ED8] p-6 text-white">
    //             <p className="text-xs font-medium opacity-90">TOTAL HOURS</p>
    //             <p className="text-2xl font-bold">{dashboardData.totalHours.toFixed(1)}h</p>
    //           </div>
    //           <div className="flex flex-col items-center justify-center rounded-full bg-[#F59E0B] p-6 text-white">
    //             <p className="text-xs font-medium opacity-90">BREAK</p>
    //             <p className="text-2xl font-bold">{dashboardData.breakHours.toFixed(2)}h</p>
    //           </div>
    //           <div className="flex flex-col items-center justify-center rounded-full bg-[#10B981] p-6 text-white">
    //             <p className="text-xs font-medium opacity-90">PRODUCTIVE</p>
    //             <p className="text-2xl font-bold">{dashboardData.productiveHours.toFixed(2)}h</p>
    //           </div>
    //           <div className="flex flex-col items-center justify-center rounded-full bg-[#EF4444] p-6 text-white">
    //             <p className="text-xs font-medium opacity-90">MONTHLY DIFF</p>
    //             <p className="text-2xl font-bold">{dashboardData.monthlyDiff.toFixed(1)}h</p>
    //           </div>
    //         </div>
    //       </CardContent>
    //     </Card>

    //     {/* Spent Hours Tracker Card */}
    //     <Card className="border border-gray-200 bg-white">
    //       <CardHeader>
    //         <CardTitle className="flex items-center gap-2 text-lg font-semibold">
    //           <Target className="h-5 w-5 text-gray-600" />
    //           Spent Hours Tracker
    //         </CardTitle>
    //       </CardHeader>
    //       <CardContent className="space-y-4">
    //         <div className="flex items-center justify-between">
    //           <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
    //             On Track
    //           </Badge>
    //         </div>
    //         <div className="space-y-2">
    //           <Progress
    //             value={(dashboardData.productiveHours / dashboardData.targetHours) * 100}
    //             className="h-3"
    //           />
    //           <div className="flex items-center justify-between text-sm">
    //             <span className="text-gray-600">
    //               {dashboardData.productiveHours.toFixed(2)}h / {dashboardData.targetHours}h
    //             </span>
    //           </div>
    //         </div>
    //         <div className="flex items-center justify-between">
    //           <span className="text-sm text-gray-600">Remaining</span>
    //           <span className="text-sm font-semibold text-blue-600">
    //             {(dashboardData.targetHours - dashboardData.productiveHours).toFixed(2)}h
    //           </span>
    //         </div>
    //       </CardContent>
    //     </Card>

    //     {/* First Check-In Card */}
    //     <Card className="border border-gray-200 bg-white">
    //       <CardHeader>
    //         <CardTitle className="flex items-center gap-2 text-lg font-semibold">
    //           <ArrowRight className="h-5 w-5 text-gray-600" />
    //           First Check-In
    //         </CardTitle>
    //       </CardHeader>
    //       <CardContent className="space-y-3">
    //         <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
    //           On Time
    //         </Badge>
    //         <div>
    //           <p className="text-2xl font-bold text-gray-900">
    //             {formatTime(dashboardData.firstCheckIn)}
    //           </p>
    //         </div>
    //         <p className="text-sm text-gray-600">
    //           Biometric ID: {dashboardData.biometricId} verified
    //         </p>
    //       </CardContent>
    //     </Card>

    //     {/* Final Check-Out Card */}
    //     <Card className="border border-gray-200 bg-white">
    //       <CardHeader>
    //         <CardTitle className="flex items-center gap-2 text-lg font-semibold">
    //           <ArrowRight className="h-5 w-5 text-gray-600" />
    //           Final Check-Out
    //         </CardTitle>
    //       </CardHeader>
    //       <CardContent className="space-y-3">
    //         <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
    //           Completed
    //         </Badge>
    //         <div>
    //           <p className="text-2xl font-bold text-gray-900">
    //             {formatTime(dashboardData.finalCheckOut)}
    //           </p>
    //         </div>
    //         <p className="text-sm text-gray-600">
    //           Biometric ID: {dashboardData.biometricId} verified
    //         </p>
    //       </CardContent>
    //     </Card>
    //   </div>

    //   {/* Break Sessions Card */}
    //   <Card className="border border-gray-200 bg-white">
    //     <CardHeader>
    //       <CardTitle className="flex items-center gap-2 text-lg font-semibold">
    //         <Clock className="h-5 w-5 text-gray-600" />
    //         Break Sessions
    //       </CardTitle>
    //     </CardHeader>
    //     <CardContent className="p-0">
    //       <div className="overflow-x-auto">
    //         <Table>
    //           <TableHeader>
    //             <TableRow className="bg-gray-50">
    //               <TableHead className="font-semibold text-gray-700">Break #</TableHead>
    //               <TableHead className="font-semibold text-gray-700">Break-In</TableHead>
    //               <TableHead className="font-semibold text-gray-700">Break-Out</TableHead>
    //               <TableHead className="font-semibold text-gray-700">Duration</TableHead>
    //             </TableRow>
    //           </TableHeader>
    //           <TableBody>
    //             {isLoading ? (
    //               <TableRow>
    //                 <TableCell colSpan={4} className="py-8 text-center text-gray-500">
    //                   Loading break sessions...
    //                 </TableCell>
    //               </TableRow>
    //             ) : dashboardData.breakSessions.length === 0 ? (
    //               <TableRow>
    //                 <TableCell colSpan={4} className="py-8 text-center text-gray-500">
    //                   No break sessions found
    //                 </TableCell>
    //               </TableRow>
    //             ) : (
    //               <>
    //                 {dashboardData.breakSessions.map((session) => (
    //                   <TableRow key={session.breakNumber} className="hover:bg-gray-50">
    //                     <TableCell className="font-medium">
    //                       Break # {session.breakNumber}
    //                     </TableCell>
    //                     <TableCell>{session.breakIn}</TableCell>
    //                     <TableCell>{session.breakOut}</TableCell>
    //                     <TableCell>{session.duration.toFixed(2)}h</TableCell>
    //                   </TableRow>
    //                 ))}
    //                 <TableRow className="bg-orange-50 font-semibold">
    //                   <TableCell colSpan={3} className="text-right">
    //                     Total Break Duration
    //                   </TableCell>
    //                   <TableCell className="text-orange-600">
    //                     {dashboardData.breakHours.toFixed(2)}h
    //                   </TableCell>
    //                 </TableRow>
    //               </>
    //             )}
    //           </TableBody>
    //         </Table>
    //       </div>
    //     </CardContent>
    //   </Card>
    // </div>
  );
};

export default EmployeeDashboard;

type SummaryPillProps = {
  label: string;
  value: string;
  bg: string;
};

const SummaryPill: React.FC<SummaryPillProps> = ({ label, value, bg }) => (
  <div className={`rounded-2xl px-5 py-3 text-center ${bg}`}>
    <p className="text-[10px] uppercase tracking-wide opacity-80">{label}</p>
    <p className="text-lg font-semibold">{value}</p>
  </div>
);

const InfoCard = ({ icon, title, time, subtitle, badge, badgeColor }: any) => {
  return (
    <div className="rounded-xl border bg-white p-3 shadow-sm transition-shadow duration-300 hover:shadow-md md:p-4">
      {/* Top Section */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-3">
          {/* Icon */}
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-lg">
            {icon}
          </div>

          {/* Text */}
          <div>
            <p className="text-sm font-medium">{title}</p>

            {/* Time (kept together) */}
            <p className="mt-1 text-lg font-bold leading-tight">{time}</p>

            {/* Subtitle */}
            {subtitle && (
              <p className="mt-1 text-xs leading-relaxed text-gray-500">
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {/* Badge */}
        {badge && (
          <span
            className={`self-start rounded-full px-3 py-1 text-xs font-medium sm:self-auto ${badgeColor}`}
          >
            {badge}
          </span>
        )}
      </div>
    </div>
  );
};
